'use client'

import { useCallback, useRef } from 'react'

import { type GkAirRecommendedProductWeb } from '@redteclab/api/clients/crosssellers-proxy-service'
import { type Metadata } from '@redteclab/api/clients/retail-media-xsell-service'

import {
  udpTrackingDataLayerPushImpressions,
  udpTrackingImpressionCacheAdd,
  type UdpTrackingProductImpression,
  useUdpTrackingBuildMetadata,
  useUdpTrackingVisibilityTracker,
} from '../../udp-tracking'

import { recommendationUdpTrackingBuildProductImpression } from './recommendationUdpTrackingBuildProductImpression'

export type TrackRecommendationUdpProductImpression = ({
  position,
  product,
  trackingKey,
}: {
  position: number
  product: GkAirRecommendedProductWeb
  trackingKey?: string
}) => (element: HTMLElement | null) => void

export const useRecommendationUdpTrackingProductImpression = ({
  adServerMetadata,
  recommendationId,
  widgetId,
}: {
  adServerMetadata?: Metadata
  recommendationId?: string
  widgetId: string
}): TrackRecommendationUdpProductImpression => {
  const buildMetadata = useUdpTrackingBuildMetadata()
  const sentProductIdsByRecommendationRef = useRef({
    productIds: new Set<string>(),
    recommendationId,
  })
  const trackVisibleProductImpressions = useCallback(
    (impressions: UdpTrackingProductImpression[]): void => {
      if (
        sentProductIdsByRecommendationRef.current.recommendationId !==
        recommendationId
      ) {
        sentProductIdsByRecommendationRef.current = {
          productIds: new Set<string>(),
          recommendationId,
        }
      }

      const unsentImpressions = impressions.filter(({ product }) => {
        const { productIds } = sentProductIdsByRecommendationRef.current

        if (!product.upid || productIds.has(product.upid)) {
          return false
        }

        productIds.add(product.upid)

        return true
      })

      if (unsentImpressions.length === 0) {
        return
      }

      for (const impression of unsentImpressions) {
        udpTrackingImpressionCacheAdd(impression)
      }

      udpTrackingDataLayerPushImpressions({
        impressions: unsentImpressions,
        metadata: buildMetadata(),
      })
    },
    [buildMetadata, recommendationId],
  )
  const visibilityTracker = useUdpTrackingVisibilityTracker({
    onVisible: trackVisibleProductImpressions,
  })

  return useCallback(
    ({ position, product, trackingKey }) =>
      (element: HTMLElement | null): void => {
        if (!recommendationId) {
          return
        }

        visibilityTracker.track({
          buildImpression: () =>
            recommendationUdpTrackingBuildProductImpression({
              isSponsored: Boolean(
                adServerMetadata?.[product.data.UID as keyof Metadata],
              ),
              position,
              product,
              recommendationId,
              widgetId,
            }),
          element,
          impressionKey: `${recommendationId}-${product.data.UID}-${trackingKey ?? 'item'}`,
        })
      },
    [adServerMetadata, recommendationId, visibilityTracker, widgetId],
  )
}
